import { Component, inject, signal, computed, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StepsModule } from 'primeng/steps';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { CloudApiService } from '../../services/cloud-api.service';
import { MessageService } from 'primeng/api';

export interface TemplateField {
  jsonPath: string;
  label: string;
  description: string;
  fieldType: 'Text' | 'Number' | 'Boolean' | 'Select' | 'Password';
  options: string[];
  required: boolean;
  defaultValue: any;
  group: string;
  step?: number;
}

export interface TemplateStep {
  step: number;
  label: string;
  description?: string;
}

export interface ConfigTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  config: any;
  fields: TemplateField[];
  steps?: TemplateStep[];
}

@Component({
  selector: 'app-config-wizard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    StepsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ToggleSwitchModule,
    SelectModule,
    InputNumberModule,
    MessageModule,
    TagModule
  ],
  template: `
    <p-dialog
      [header]="currentStepTitle()"
      [visible]="visible"
      (onHide)="onClose()"
      [modal]="true"
      [style]="{ width: '700px', maxHeight: '80vh' }"
      [contentStyle]="{ overflow: 'auto' }"
      appendTo="body">

      <!-- Steps indicator -->
      <div class="mb-4">
        <p-steps [model]="dynamicSteps()" [activeIndex]="activeStep()" [readonly]="true"></p-steps>
      </div>

      <!-- Step: Select Template (always index 0) -->
      @if (activeStep() === 0) {
        @if (loadingTemplates()) {
          <div class="flex items-center justify-center p-8">
            <i class="pi pi-spin pi-spinner text-4xl"></i>
          </div>
        } @else if (templates().length === 0) {
          <p-message severity="info">No templates available for this device. Templates are defined in the device program.</p-message>
        } @else {
          <div class="template-grid">
            @for (tpl of templates(); track tpl.id) {
              <div
                class="template-card"
                [class.selected]="selectedTemplate()?.id === tpl.id"
                (click)="selectTemplate(tpl)">
                <div class="template-card-header">
                  <span class="template-name">{{ tpl.name }}</span>
                  @if (tpl.category) {
                    <p-tag [value]="tpl.category" [rounded]="true" severity="info"></p-tag>
                  }
                </div>
                <p class="template-desc">{{ tpl.description }}</p>
                <div class="template-meta">
                  <span><i class="pi pi-list"></i> {{ tpl.fields.length || 0 }} configurable fields</span>
                </div>
              </div>
            }
          </div>
        }
      }

      <!-- Configure step(s) -->
      @if (isConfigureStep()) {
        @if (!selectedTemplate()?.fields?.length) {
          <p-message severity="info">This template has no configurable fields. Click Next to review.</p-message>
        } @else {
          @if (currentConfigStepDescription()) {
            <p class="text-color-secondary mb-3">{{ currentConfigStepDescription() }}</p>
          }
          @for (group of currentFieldGroups(); track group.name) {
            <div class="field-group">
              @if (group.name) {
                <h4 class="field-group-title">{{ group.name }}</h4>
              }
              @for (field of group.fields; track field.jsonPath) {
                <div class="field-row">
                  <label class="field-label">
                    {{ field.label }}
                    @if (field.required) {
                      <span class="required">*</span>
                    }
                  </label>
                  @if (field.description) {
                    <small class="field-desc">{{ field.description }}</small>
                  }

                  @switch (field.fieldType) {
                    @case ('Text') {
                      <input pInputText
                        [ngModel]="fieldValues()[field.jsonPath] ?? ''"
                        (ngModelChange)="setFieldValue(field.jsonPath, $event)"
                        class="w-full" />
                    }
                    @case ('Number') {
                      <p-inputnumber
                        [ngModel]="fieldValues()[field.jsonPath] ?? 0"
                        (ngModelChange)="setFieldValue(field.jsonPath, $event)"
                        class="w-full">
                      </p-inputnumber>
                    }
                    @case ('Boolean') {
                      <p-toggleswitch
                        [ngModel]="fieldValues()[field.jsonPath] ?? false"
                        (ngModelChange)="setFieldValue(field.jsonPath, $event)">
                      </p-toggleswitch>
                    }
                    @case ('Select') {
                      <p-select
                        [ngModel]="fieldValues()[field.jsonPath] ?? ''"
                        (ngModelChange)="setFieldValue(field.jsonPath, $event)"
                        [options]="field.options"
                        class="w-full">
                      </p-select>
                    }
                    @case ('Password') {
                      <input pInputText
                        [ngModel]="fieldValues()[field.jsonPath] ?? ''"
                        (ngModelChange)="setFieldValue(field.jsonPath, $event)"
                        type="password"
                        class="w-full" />
                    }
                  }
                </div>
              }
            </div>
          }
        }
      }

      <!-- Review & Deploy (always last step) -->
      @if (isReviewStep()) {
        <div class="review-section">
          <h4>Template: {{ selectedTemplate()?.name }}</h4>

          @if (reviewFields().length > 0) {
            <table class="review-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                @for (rf of reviewFields(); track rf.path) {
                  <tr>
                    <td>{{ rf.label }}</td>
                    <td class="review-value">{{ rf.value }}</td>
                  </tr>
                }
              </tbody>
            </table>
          }

          <p-message
            severity="warn"
            class="mt-3">Applying this template will overwrite the current device configuration and trigger a restart.
          </p-message>
        </div>
      }

      <ng-template #footer>
        <div class="flex justify-between w-full">
          <p-button
            *ngIf="activeStep() > 0"
            label="Back"
            icon="pi pi-arrow-left"
            [text]="true"
            (onClick)="prevStep()">
          </p-button>
          <div class="flex-1"></div>
          <p-button
            *ngIf="!isReviewStep()"
            label="Next"
            icon="pi pi-arrow-right"
            iconPos="right"
            [disabled]="!canProceed()"
            (onClick)="nextStep()">
          </p-button>
          <p-button
            *ngIf="isReviewStep()"
            label="Deploy & Restart"
            icon="pi pi-cloud-upload"
            severity="success"
            [loading]="deploying()"
            (onClick)="deploy()">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .template-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }

    .template-card {
      border: 2px solid var(--surface-border);
      border-radius: var(--content-border-radius);
      padding: 1rem;
      cursor: pointer;
      transition: border-color 0.2s, background-color 0.2s;

      &:hover {
        border-color: var(--primary-color);
        background-color: var(--surface-hover);
      }

      &.selected {
        border-color: var(--primary-color);
        background-color: color-mix(in srgb, var(--primary-color) 8%, transparent);
      }
    }

    .template-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.5rem;
    }

    .template-name {
      font-weight: 600;
      font-size: 1.05rem;
    }

    .template-desc {
      color: var(--text-color-secondary);
      font-size: 0.9rem;
      margin: 0 0 0.5rem 0;
    }

    .template-meta {
      font-size: 0.8rem;
      color: var(--text-color-secondary);
    }

    .field-group {
      margin-bottom: 1.25rem;
    }

    .field-group-title {
      font-size: 1rem;
      font-weight: 600;
      margin: 0 0 0.75rem 0;
      padding-bottom: 0.25rem;
      border-bottom: 1px solid var(--surface-border);
    }

    .field-row {
      margin-bottom: 1rem;
    }

    .field-label {
      display: block;
      font-weight: 500;
      margin-bottom: 0.25rem;
    }

    .required {
      color: var(--red-500);
    }

    .field-desc {
      display: block;
      color: var(--text-color-secondary);
      margin-bottom: 0.35rem;
    }

    .review-table {
      width: 100%;
      border-collapse: collapse;

      th, td {
        text-align: left;
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid var(--surface-border);
      }

      th {
        font-weight: 600;
        background: var(--surface-ground);
      }
    }

    .review-value {
      font-family: monospace;
    }
  `]
})
export class ConfigWizardComponent implements OnInit {
  @Input() visible = false;
  @Input() deviceId: string = '';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() templateApplied = new EventEmitter<void>();

  private cloudApi = inject(CloudApiService);
  private messageService = inject(MessageService);

  activeStep = signal(0);
  templates = signal<ConfigTemplate[]>([]);
  loadingTemplates = signal(false);
  selectedTemplate = signal<ConfigTemplate | null>(null);
  fieldValues = signal<Record<string, any>>({});
  deploying = signal(false);

  /** Dynamic steps: [Select, ...templateSteps or Configure, Review] */
  dynamicSteps = computed(() => {
    const tpl = this.selectedTemplate();
    const steps: { label: string }[] = [{ label: 'Select Template' }];

    if (tpl?.steps?.length) {
      for (const s of tpl.steps) {
        steps.push({ label: s.label });
      }
    } else {
      steps.push({ label: 'Configure' });
    }

    steps.push({ label: 'Review & Deploy' });
    return steps;
  });

  /** Total number of steps */
  totalSteps = computed(() => this.dynamicSteps().length);

  /** Last step index (review) */
  reviewStepIndex = computed(() => this.totalSteps() - 1);

  /** Current step title for dialog header */
  currentStepTitle = computed(() => {
    const step = this.activeStep();
    if (step === 0) return 'Choose a Configuration Template';
    if (step === this.reviewStepIndex()) return 'Review & Deploy';

    const tpl = this.selectedTemplate();
    if (tpl?.steps?.length) {
      const tplStep = tpl.steps[step - 1];
      return tplStep?.label ?? 'Configure';
    }
    return 'Configure Template Fields';
  });

  /** Whether current step is a configure step (between select and review) */
  isConfigureStep = computed(() => {
    const step = this.activeStep();
    return step > 0 && step < this.reviewStepIndex();
  });

  /** Whether current step is the review step */
  isReviewStep = computed(() => this.activeStep() === this.reviewStepIndex());

  /** Description for the current configure step (from template steps) */
  currentConfigStepDescription = computed(() => {
    const tpl = this.selectedTemplate();
    if (!tpl?.steps?.length) return '';
    const tplStep = tpl.steps[this.activeStep() - 1];
    return tplStep?.description ?? '';
  });

  /** Field groups for the current configure step */
  currentFieldGroups = computed(() => {
    const tpl = this.selectedTemplate();
    if (!tpl?.fields?.length) return [];

    const step = this.activeStep();
    let fields: TemplateField[];

    if (tpl.steps?.length) {
      // Multi-step: filter fields by step number (step - 1 maps to template step index)
      const templateStepNum = step; // activeStep 1 = template step 1, etc.
      fields = tpl.fields.filter(f => f.step === templateStepNum);
    } else {
      // Single configure step: show all fields
      fields = tpl.fields;
    }

    const groups = new Map<string, TemplateField[]>();
    for (const field of fields) {
      const groupName = field.group || '';
      if (!groups.has(groupName)) groups.set(groupName, []);
      groups.get(groupName)!.push(field);
    }
    return Array.from(groups.entries()).map(([name, groupFields]) => ({ name, fields: groupFields }));
  });

  reviewFields = computed(() => {
    const tpl = this.selectedTemplate();
    const vals = this.fieldValues();
    if (!tpl?.fields) return [];

    return tpl.fields.map(f => ({
      path: f.jsonPath,
      label: f.label,
      value: f.fieldType === 'Password' ? '••••••' : (vals[f.jsonPath] ?? f.defaultValue ?? '-')
    }));
  });

  canProceed = computed(() => {
    const step = this.activeStep();
    if (step === 0) return this.selectedTemplate() !== null;

    if (this.isConfigureStep()) {
      const tpl = this.selectedTemplate();
      const vals = this.fieldValues();
      if (!tpl) return false;

      if (tpl.steps?.length) {
        // Validate only fields for the current template step
        const templateStepNum = step;
        const stepFields = tpl.fields.filter(f => f.step === templateStepNum);
        return stepFields.filter(f => f.required).every(f => {
          const v = vals[f.jsonPath];
          return v !== undefined && v !== null && v !== '';
        });
      } else {
        // Validate all required fields
        return tpl.fields.filter(f => f.required).every(f => {
          const v = vals[f.jsonPath];
          return v !== undefined && v !== null && v !== '';
        });
      }
    }

    return true;
  });

  ngOnInit() {
    this.loadTemplates();
  }

  async loadTemplates() {
    if (!this.deviceId) return;
    this.loadingTemplates.set(true);
    try {
      const res = await this.cloudApi.getDeviceTemplates(this.deviceId);
      this.templates.set(res.templates || []);
    } catch (e) {
      console.error('Failed to load templates', e);
    } finally {
      this.loadingTemplates.set(false);
    }
  }

  selectTemplate(tpl: ConfigTemplate) {
    this.selectedTemplate.set(tpl);
    // Pre-populate field values with defaults
    const defaults: Record<string, any> = {};
    for (const field of tpl.fields || []) {
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        defaults[field.jsonPath] = field.defaultValue;
      }
    }
    this.fieldValues.set(defaults);
  }

  setFieldValue(path: string, value: any) {
    this.fieldValues.update(v => ({ ...v, [path]: value }));
  }

  nextStep() {
    if (this.activeStep() < this.reviewStepIndex()) {
      this.activeStep.update(s => s + 1);
    }
  }

  prevStep() {
    if (this.activeStep() > 0) {
      this.activeStep.update(s => s - 1);
    }
  }

  async deploy() {
    const tpl = this.selectedTemplate();
    if (!tpl) return;

    this.deploying.set(true);
    try {
      await this.cloudApi.applyDeviceTemplate(this.deviceId, tpl.id, this.fieldValues());
      this.messageService.add({
        severity: 'success',
        summary: 'Template Applied',
        detail: `Template "${tpl.name}" has been applied. The device will restart.`
      });
      this.templateApplied.emit();
      this.onClose();
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: e?.error?.message || 'Failed to apply template'
      });
    } finally {
      this.deploying.set(false);
    }
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.activeStep.set(0);
  }
}
